/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import { useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { jsx } from "@emotion/core"
import { Button, Modal, Dropdown, Spin } from "@duxton-web/ui"
import { DownloadUniversal } from "@duxton-web/icons"
import { actionCreator } from "store/store"
import { AppState } from "store/store.type"
import { useTranslationsMap } from "common/utils/i18n"
import { MerchantStoreInfo } from "types/models/menu"
import uniq from "lodash/uniq"
import { TroyWebTracker } from "utils/tracking"
import { EventActionEng, EventCategory } from "constants/tracking"
import FeedbackBulkDownload from "./FeedbackBulkDownload"
import {
  LoadingDescriptionStyles,
  LoadingTitleStyles,
  ModalBodyStyles,
} from "./FeedbackDownload.styles"

const FeedbackDownload = () => {
  const dispatch = useDispatch()
  const [isBulkDownload, setIsBulkDownload] = useState(false)
  const { isLoadingCsv } = useSelector((state: AppState) => state.feedback)
  const outlets = useSelector<AppState, MerchantStoreInfo[] | undefined>(
    state => state.menu.outlets,
  )
  const isFetchingOutlets = useSelector<AppState, boolean>(
    state => state.menu.isFetchingOutlets,
  )
  const textMap = useTranslationsMap({
    download: "common.download",
    heading: "feedback.download.heading",
    body: "feedback.download.body",
    cancel: "menu.buttons.cancel",
    errorHeading: "feedback.download.error.heading",
    errorBody: "feedback.download.error.body",
  })

  const showBulkDownload =
    !isFetchingOutlets &&
    !!outlets &&
    outlets.length > 1 &&
    uniq(outlets.map(o => o.city)).length > 1

  const download = () => {
    dispatch(
      actionCreator.feedback.requestCsvReport({
        textMap: {
          errorHeading: textMap.errorHeading,
          errorBody: textMap.errorBody,
        },
      }),
    )
  }

  const cancelDownload = () => {
    dispatch(actionCreator.feedback.cancelCsvReport())
  }

  const handleMenuClick = (e: { key: string }) => {
    switch (e.key) {
      case "bulk":
        TroyWebTracker.sendEvent({
          category: EventCategory.feedbackHomePage,
          action: EventActionEng.showBulkDownloadModal,
        })
        setIsBulkDownload(true)
        break
      default:
        break
    }
  }

  const menu = [
    {
      label: "Bulk Download",
      key: "bulk",
    },
  ]

  return (
    <div>
      {showBulkDownload ? (
        <Dropdown.Button
          menu={{
            items: menu,
            onClick: handleMenuClick,
          }}
          onClick={download}
          aria-label="download"
        >
          <DownloadUniversal />
          {textMap.download}
        </Dropdown.Button>
      ) : (
        <Button onClick={download} aria-label="download">
          <DownloadUniversal />
          {textMap.download}
        </Button>
      )}

      <FeedbackBulkDownload
        visible={isBulkDownload}
        close={() => setIsBulkDownload(false)}
      />

      <Modal
        open={!isBulkDownload && isLoadingCsv}
        footer={
          <Button
            type="secondary"
            onClick={cancelDownload}
            aria-label="cancel download"
          >
            {textMap.cancel}
          </Button>
        }
      >
        <div css={ModalBodyStyles}>
          <Spin />

          <div css={LoadingTitleStyles}>{textMap.heading}</div>

          <div css={LoadingDescriptionStyles}>{textMap.body}</div>
        </div>
      </Modal>
    </div>
  )
}

export default FeedbackDownload
